import Alert from '@pelckmans/elementis/components/alert';
import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import Heading from '@pelckmans/elementis/components/heading';
import TextField from '@pelckmans/elementis/components/text-field';
import { useToast } from '@pelckmans/elementis/components/toast';
import { Dispatch, SetStateAction, useContext, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { fetchGroups, Group } from '../../../../api/groups';
import { UserSchool } from '../../../../api/schools';
import { TOAST_ID } from '../../../../constants/ToastIds';
import ModalContext from '../../../../contexts/ModalContext';
import { createGroup } from '../../api/MyGroupsApi';
import { SchoolYear, sortGroups } from '../../context/MyGroupsContext';
import SchoolSearchSelector from '../../components/SchoolSearchSelector';
import SubjectSearchSelector from '../../components/SubjectSearchSelector';

type Props = {
  id: string;
  schoolYear: SchoolYear;
  onGroupsChange: Dispatch<SetStateAction<Group[] | undefined>>;
};

type FormValues = {
  groupName: string;
  school: UserSchool | null;
  subjects: { id: string; name: string }[];
};

const FORM_ID = 'add-personal-group-form';

function AddPersonalGroupModal({ id, schoolYear, onGroupsChange }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { closeModal } = useContext(ModalContext);
  const { addToast } = useToast();
  const [alertState, setAlert] = useState<'failed' | 'invalid' | undefined>();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isSubmitted },
    getValues,
  } = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues: { groupName: '', school: null, subjects: [] },
  });

  const handleClose = () => closeModal(id);

  const onSubmit = async () => {
    const { groupName, school, subjects } = getValues();

    try {
      await createGroup({
        name: groupName,
        school: { id: school!.id, isSmartschool: school!.isSmartschool },
        subjects: subjects.map(s => s.id),
        schoolYear,
      });

      const groups = await fetchGroups(schoolYear);

      onGroupsChange(sortGroups(groups, language));
      addToast({
        id: TOAST_ID.PERSONAL_GROUP_SUCCESS,
        variant: 'success',
        title: t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.TOAST.SUCCESS.TITLE'),
        children: t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.TOAST.SUCCESS.MESSAGE'),
      });
      handleClose();
    } catch {
      setAlert('failed');
    }
  };

  const onInvalid = () => setAlert('invalid');

  const renderAlert = () => {
    if (alertState === 'invalid') {
      return (
        <Alert prominence="danger">{t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.FORM.ALERT.INVALID')}</Alert>
      );
    }
    if (alertState === 'failed') {
      return (
        <Alert
          prominence="danger"
          actions={{
            'aria-label': t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.FORM.ALERT.FAILED.ACTION_A11Y_LABEL'),
            role: 'button',
            children: (
              <Button prominence="danger" onClick={handleSubmit(onSubmit, onInvalid)} busy={isSubmitting}>
                {t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.FORM.ALERT.FAILED.ACTION')}
              </Button>
            ),
          }}
        >
          {t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.FORM.ALERT.FAILED.MESSAGE')}
        </Alert>
      );
    }

    return <></>;
  };

  return (
    <Dialog
      id={id}
      modal
      size="md"
      onClose={handleClose}
      aria-label={t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.A11Y_LABEL')}
    >
      <Dialog.Header
        title={t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.HEADER.TITLE')}
        onClose={handleClose}
        aria-label={t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.HEADER.ACTIONS.CLOSE')}
      />
      <Dialog.Body
        alertGroupProps={
          alertState
            ? {
                'aria-label': t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.FORM.GROUP.ALERT.A11Y_LABEL'),
                role: undefined,
                children: renderAlert(),
              }
            : undefined
        }
      >
        <form
          noValidate
          id={FORM_ID}
          name={FORM_ID}
          onSubmit={handleSubmit(onSubmit, onInvalid)}
          className="add-group-form"
        >
          <Heading level={4} removeSpacing="all" className="modal-content-text-title">
            {t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.TITLE')}
          </Heading>
          <div className="form-fields">
            <TextField
              labelText={t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.FORM.GROUP.LABEL')}
              placeholder={t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.FORM.GROUP.PLACEHOLDER')}
              maxLength={100}
              error={!!errors.groupName}
              errorText={errors.groupName?.message}
              required
              {...register('groupName', {
                required: t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.FORM.GROUP.ERROR') as string,
              })}
            />

            <Controller
              control={control}
              name="school"
              rules={{ required: t('PAGES.MY_GROUPS.SCHOOL.ERROR') as string }}
              render={({ field }) => (
                <SchoolSearchSelector
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  errorText={errors.school?.message}
                />
              )}
            />

            <Controller
              control={control}
              name="subjects"
              rules={{
                validate: v => v.length > 0 || t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.FORM.SUBJECT.ERROR'),
              }}
              render={({ field }) => (
                <SubjectSearchSelector
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  errorText={errors.subjects?.message}
                />
              )}
            />
          </div>
        </form>
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={{
          'aria-label': undefined,
          role: undefined,
          children: (
            <>
              <Button prominence="neutral" onClick={handleClose} key="cancel">
                {t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.FOOTER.ACTIONS.CANCEL')}
              </Button>
              <Button prominence="success" type="submit" form={FORM_ID} key="submit" busy={isSubmitting || isSubmitted}>
                {t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.FOOTER.ACTIONS.SUBMIT')}
              </Button>
            </>
          ),
        }}
      />
    </Dialog>
  );
}

export default AddPersonalGroupModal;
